import {
  IDepot,
  ISupplierCompanyPermissions,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {ESupplierUserPermissions} from 'constants/index';

// todo this needs to change based on campany permissions form

export class PermissionsService {
  private readonly userPermissions: Set<string>;
  private readonly companyPermissions?: ISupplierCompanyPermissions;
  private readonly user?: ISupplierUserProfile;

  constructor(user?: ISupplierUserProfile) {
    this.userPermissions = new Set(
      user?.permissions?.map((permission) => permission.name),
    );
    this.companyPermissions = user?.companyPermissions;
    this.user = user;
  }

  hasPermission(permission: ESupplierUserPermissions) {
    return this.userPermissions.has(permission);
  }

  shouldShowAnalytics() {
    if (!this.companyPermissions) return false;
    return (
      (this.companyPermissions.quotes &&
        this.userPermissions.has(ESupplierUserPermissions.QUOTE_READ)) ||
      (this.companyPermissions.orders &&
        this.userPermissions.has(ESupplierUserPermissions.ORDER_READ))
    );
  }

  shouldShowCustomers() {
    if (!this.companyPermissions) return false;
    return (
      this.companyPermissions.customers &&
      this.userPermissions.has(ESupplierUserPermissions.CUSTOMER_READ)
    );
  }

  shouldShowCustomerApplications() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.customerApplications;
  }

  shouldShowCustomerWallet() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.customerWallet;
  }

  shouldShowCustomerFieldsAndLayers() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.customerFieldsAndLayers;
  }

  shouldShowCustomerPrograms() {
    const viewonly = this.shouldShowProgramsViewOnly();
    if (viewonly) return true;
    if (!this.companyPermissions) return false;
    return this.companyPermissions.customerPrograms;
  }

  shouldShowProducts() {
    if (!this.companyPermissions) return false;
    return (
      this.companyPermissions.productsGlobalViewOnly ||
      (this.companyPermissions.products &&
        this.userPermissions.has(ESupplierUserPermissions.PRODUCT_READ))
    );
  }

  shouldShowProductsGlobalViewOnly() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.productsGlobalViewOnly;
  }

  shouldShowInventory() {
    if (!this.companyPermissions) return false;
    return (
      this.companyPermissions.products &&
      this.userPermissions.has(ESupplierUserPermissions.DEPOT_PRODUCT_READ)
    );
  }

  shouldShowQuotes() {
    if (!this.companyPermissions) return false;
    return (
      this.companyPermissions.quotes &&
      this.userPermissions.has(ESupplierUserPermissions.QUOTE_READ)
    );
  }

  shouldShowOrders() {
    if (!this.companyPermissions) return false;
    return (
      this.companyPermissions.orders &&
      this.userPermissions.has(ESupplierUserPermissions.ORDER_READ)
    );
  }

  shouldShowPurchaseOrders() {
    if (this.user?.isKhulaAdmin) return true;
    if (!this.companyPermissions) return false;
    return this.companyPermissions.purchaseOrders || !!this.user?.isAECI;
  }

  shouldShowCompany() {
    if (!this.companyPermissions) return false;
    return this.userPermissions.has(ESupplierUserPermissions.COMPANY_READ);
  }

  shouldShowDepots() {
    if (!this.companyPermissions) return false;
    return this.userPermissions.has(ESupplierUserPermissions.DEPOT_READ);
  }

  shouldShowTeam() {
    if (!this.companyPermissions) return false;
    return this.userPermissions.has(ESupplierUserPermissions.TEAM_READ);
  }

  shouldShowOnboard() {
    if (!this.companyPermissions) return false;
    return (
      this.companyPermissions.onboarding ||
      this.userPermissions.has(ESupplierUserPermissions.COMPANY_ONBOARD)
    );
  }

  getEditableDepots(allDepots: IDepot[]): IDepot[] {
    if (!this.user) return [];
    return !this.user.isKhulaAdmin && !this.user.isAdmin
      ? this.user.depots!
      : allDepots;
  }

  shouldShowWallet() {
    if (!this.companyPermissions) return false;
    return !!this.user?.isAECI;
  }

  shouldShowOrderBundles() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.orderBundles || !!this.user?.isKhulaAdmin;
  }

  shouldShowFundingApplications() {
    const viewonly = this.shouldShowFundingApplicationsViewOnly();
    if (!this.companyPermissions) return false;
    if (viewonly) return true;
    else return this.companyPermissions.fundingApplications;
  }

  shouldShowFundingApplicationsViewOnly() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.fundingApplicationsViewOnly;
  }

  shouldShowPrograms() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.programs;
  }

  shouldShowProgramsViewOnly() {
    if (!this.companyPermissions) return false;
    return this.companyPermissions.programsViewOnly;
  }

  shouldShowGroups() {
    // Interim gate: visible to admin users until GROUP_READ permission is wired from backend
    return !!this.user?.isKhulaAdmin || !!this.user?.isAdmin;
  }

  shouldShowGroupsWrite() {
    return !!this.user?.isKhulaAdmin || !!this.user?.isAdmin;
  }
}
